import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, ExternalLink, Home, Eye, CheckCircle2, AlertTriangle, Link2, Sparkles, Loader2, Ban, ShieldX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ImageViewer } from '@/components/ui/image-viewer';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { EnrichmentForm } from '@/components/enrichment/EnrichmentForm';
import { PublishButton, UnpublishButton, PublishedStatusBadge, LocationPreviewDialog } from '@/components/publishing';
import { SyncToScoutAroundButton } from '@/components/publishing/SyncToScoutAroundButton';
import { PendingSyncBadge } from '@/components/publishing/PendingSyncBadge';
import {
  PricingInfoCard,
  CopyableCoordinates,
  SyncTimestampsCard,
  HostContactCard,
  AiContentPanel,
} from '@/components/synced-locations';
import { PartnerTaxonomyCard } from '@/components/synced-locations/PartnerTaxonomyCard';
import { PinAddressCard } from '@/components/synced-locations/PinAddressCard';
import { LocationPartnerLine } from '@/components/synced-locations/LocationPartnerLine';
import { StreetLevelCard } from '@/components/synced-locations/StreetLevelCard';
import { BlockDialog } from '@/components/synced-locations/BlockDialog';
import { BlockedStatusBadge } from '@/components/synced-locations/BlockedStatusBadge';
import { useBlockLocation, useUnblockLocation } from '@/api/block';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSyncedLocation, useRemoveLocationImage } from '@/api/synced-locations';
import { useToast } from '@/components/ui/use-toast';
import { useSingleImageReanalysis } from '@/hooks/useSingleImageReanalysis';
import { useBulkImageRemoval } from '@/hooks/useBulkImageRemoval';
import { isFileImportPartner } from '@/lib/partner-type';
import { describeImageRemoval, imageRemovalErrorText, sortGalleryByOrder } from '@/lib/image-removal';
import { useEnrichment } from '@/api/enrichment';
import { usePublicationStatus, usePreview } from '@/api/publishing';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useTagSuggestions, useAcceptTags, useRejectTags, useRemoveTag, useAvailableTags, useReanalyzeAllImages } from '@/api/tag-suggestions';

// Strip markdown for plain text contexts
function stripMarkdown(text: string): string {
  return text.replace(/[*_~`]/g, '');
}

export function SyncedLocationDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { data: location, isLoading: isLoadingLocation, error: locationError } = useSyncedLocation(id ?? '');
  // The gallery as every grid on the page shows it; the removing card's index is looked up in this order
  const sortedPartnerImages = useMemo(
    () => sortGalleryByOrder(location?.partnerImages ?? []),
    [location?.partnerImages]
  );
  const { data: enrichment, isLoading: isLoadingEnrichment } = useEnrichment(id ?? '');
  const { data: publicationStatus } = usePublicationStatus(id ?? '');
  const { data: previewData } = usePreview(id ?? '');
  const { data: tagData } = useTagSuggestions(id ?? '');
  const { data: availableTags } = useAvailableTags();
  const acceptMutation = useAcceptTags();
  const rejectMutation = useRejectTags();
  const removeMutation = useRemoveTag();
  const reanalyzeAllImagesMutation = useReanalyzeAllImages();
  const blockMutation = useBlockLocation();
  const unblockMutation = useUnblockLocation();
  const removeImageMutation = useRemoveLocationImage();
  const { toast } = useToast();

  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);

  // Track which tags are being processed (accept/reject)
  const [processingTags, setProcessingTags] = useState<Set<number>>(new Set());
  // Track which tags are being removed (Phase 26)
  const [removingTags, setRemovingTags] = useState<Set<number>>(new Set());
  // Track which image is being removed; the form disables every Remove while it is set
  const [removingImageIndex, setRemovingImageIndex] = useState<number | undefined>(undefined);
  // Removing several images at once; the gallery reads removingImageIndex -1 meanwhile, so it stays locked
  const { progress: bulkRemovalProgress, removeImages } = useBulkImageRemoval(id);

  const startProcessing = useCallback((tagId: number) => {
    setProcessingTags((prev) => new Set(prev).add(tagId));
  }, []);

  const stopProcessing = useCallback((tagId: number) => {
    setProcessingTags((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
  }, []);

  // Handler for accepting an image tag
  const handleAcceptImageTag = useCallback((tagId: number, imageIndex: number) => {
    startProcessing(tagId);
    acceptMutation.mutate(
      { locationId: id ?? '', tagIds: [tagId], scope: 'image', imageIndex },
      { onSettled: () => { stopProcessing(tagId); } }
    );
  }, [id, acceptMutation, startProcessing, stopProcessing]);

  // Handler for rejecting an image tag
  const handleRejectImageTag = useCallback((tagId: number, imageIndex: number) => {
    startProcessing(tagId);
    rejectMutation.mutate(
      { locationId: id ?? '', tagIds: [tagId], scope: 'image', imageIndex },
      { onSettled: () => { stopProcessing(tagId); } }
    );
  }, [id, rejectMutation, startProcessing, stopProcessing]);

  // Handler for removing an image tag (Phase 26)
  const handleRemoveImageTag = useCallback((tagId: number, imageIndex: number) => {
    setRemovingTags((prev) => new Set(prev).add(tagId));
    removeMutation.mutate(
      { locationId: id ?? '', tagId, scope: 'image', imageIndex },
      {
        onSettled: () => {
          setRemovingTags((prev) => {
            const next = new Set(prev);
            next.delete(tagId);
            return next;
          });
        },
      }
    );
  }, [id, removeMutation]);

  // Handler for re-analyzing all images
  const handleReanalyzeAllImages = useCallback(() => {
    if (!id) return;
    reanalyzeAllImagesMutation.mutate(id);
  }, [id, reanalyzeAllImagesMutation]);

  // Re-analyzing a single image: in progress until its analysis job finishes
  const handleSingleImageStartFailed = useCallback((error: unknown) => {
    toast({
      variant: 'destructive',
      title: 'Could not start the photo analysis',
      description: error instanceof Error ? error.message : 'Try again in a moment.',
    });
  }, [toast]);
  const { reanalyzingImageIndex, start: handleReanalyzeSingleImage } = useSingleImageReanalysis(
    id,
    tagData?.status,
    handleSingleImageStartFailed
  );

  // Handler for removing a partner image. The grid reports the URL the confirm
  // dialog was opened for, and the server is addressed by it; the index is
  // looked up only to mark the removing card and lock the gallery.
  // The mutation's own onSuccess refetches every reader of the listing.
  const handleRemoveImage = useCallback((url: string) => {
    if (!id) return;
    // -1 when a refetch has already taken the image out of the gallery: the
    // gallery still locks, no card spins, and the server answers that the
    // image is not on the listing.
    setRemovingImageIndex(sortedPartnerImages.findIndex((image) => image.url === url));
    removeImageMutation.mutate(
      { syncedLocationId: id, url },
      {
        onSuccess: (result) => {
          toast({ title: 'Image removed', description: describeImageRemoval(result) });
        },
        onError: (error) => {
          toast({ variant: 'destructive', title: 'Image not removed', description: imageRemovalErrorText(error) });
        },
        onSettled: () => {
          setRemovingImageIndex(undefined);
        },
      }
    );
  }, [id, sortedPartnerImages, removeImageMutation, toast]);

  if (isLoadingLocation) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (locationError || !location) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { void navigate(-1); }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Locations
        </Button>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">Location not found or failed to load.</p>
        </div>
      </div>
    );
  }

  // Calculate readiness for publishing
  const isEnriched = location.enrichmentStatus === 'enriched';
  const hasImages = (location.partnerImages?.length ?? 0) > 0;
  const accountEnabled = location.partnerAccount?.enabled ?? false;
  const isBlocked = location.isBlocked ?? false;
  const isReadyToPublish = isEnriched && hasImages && accountEnabled && !isBlocked;
  const isPublished = location.publicationStatus === 'published';

  // Block/unblock handlers
  const handleBlock = (reason: string): void => {
    blockMutation.mutate(
      { syncedLocationId: location.id, reason },
      {
        onSuccess: () => {
          setBlockDialogOpen(false);
        },
      }
    );
  };

  const handleUnblock = (): void => {
    unblockMutation.mutate(location.id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { void navigate(-1); }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{location.name}</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {location.address?.city && location.address.country
                  ? `${location.address.city}, ${location.address.country}`
                  : location.address?.full ?? 'No address'}
              </span>
              <span aria-hidden="true">·</span>
              <LocationPartnerLine partnerAccount={location.partnerAccount} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Blocked status badge */}
          {isBlocked && (
            <BlockedStatusBadge reason={location.blockedReason} />
          )}
          <Badge variant={location.enrichmentStatus === 'enriched' ? 'success' : 'outline'}>
            {location.enrichmentStatus === 'enriched' ? 'Enriched' : 'Pending Enrichment'}
          </Badge>
          <PublishedStatusBadge
            status={location.publicationStatus}
            scoutaroundUrl={publicationStatus?.scoutaroundUrl}
          />
          {/* Block/Unblock button */}
          {isBlocked ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnblock}
              disabled={unblockMutation.isPending}
              data-testid="unblock-btn"
            >
              {unblockMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ShieldX className="mr-2 h-4 w-4" />
              )}
              Unblock
            </Button>
          ) : isPublished ? (
            // Published locations cannot be blocked - show disabled button with tooltip
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      data-testid="block-btn"
                    >
                      <Ban className="mr-2 h-4 w-4" />
                      Block
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Must unpublish first</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setBlockDialogOpen(true); }}
              data-testid="block-btn"
            >
              <Ban className="mr-2 h-4 w-4" />
              Block
            </Button>
          )}
          {location.externalUrl && (
            <a href={location.externalUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <ExternalLink className="mr-2 h-4 w-4" />
                View on Partner
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Blocked Alert Banner */}
      {isBlocked && (
        <Alert variant="destructive" data-testid="blocked-alert">
          <Ban className="h-4 w-4" />
          <AlertTitle>This location is blocked</AlertTitle>
          <AlertDescription>
            {location.blockedReason && (
              <p className="mt-1">{location.blockedReason}</p>
            )}
            <p className="mt-2 text-sm">
              Blocked locations cannot be published to ScoutAround. Click &quot;Unblock&quot; to allow publishing.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Validation Issues Alert */}
      {previewData && !previewData.success && 'validationErrors' in previewData && (previewData.validationErrors?.length ?? 0) > 0 && (
        <Alert variant="destructive" data-testid="validation-issues-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>This location cannot be published</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 list-disc pl-4 space-y-1">
              {(previewData.validationErrors ?? []).map((err, index) => (
                <li key={index}>{err.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Publishing Status Banner - Prominent at the top */}
      <Card className={(publicationStatus?.isPublished || isReadyToPublish) ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
        <CardContent className="py-4">
          {publicationStatus?.isPublished ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                  <ExternalLink className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-green-800">Published on ScoutAround</p>
                    {location.hasPendingSync && <PendingSyncBadge />}
                  </div>
                  <p className="text-sm text-green-600">
                    Published on {publicationStatus.publishedAt ? new Date(publicationStatus.publishedAt).toLocaleDateString() : 'Unknown'}
                    {publicationStatus.scoutaroundUrl && (
                      <>
                        {' · '}
                        <a
                          href={publicationStatus.scoutaroundUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary hover:underline"
                        >
                          View live listing
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {location.hasPendingSync && (
                  <SyncToScoutAroundButton syncedLocationId={location.id} />
                )}
                <UnpublishButton
                  syncedLocationId={location.id}
                  locationName={stripMarkdown(location.name)}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${isReadyToPublish ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {isReadyToPublish ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className={`font-medium ${isReadyToPublish ? 'text-green-800' : 'text-amber-800'}`}>
                    {isReadyToPublish ? 'Ready for Publishing' : 'Not Ready for Publishing'}
                  </p>
                  {!isReadyToPublish && (
                    <p className="text-sm text-amber-600">
                      {isBlocked && 'Location is blocked'}
                      {!isBlocked && !isEnriched && 'Needs enrichment'}
                      {!isBlocked && isEnriched && !hasImages && 'No images available'}
                      {!isBlocked && isEnriched && hasImages && !accountEnabled && 'Partner account disabled'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setPreviewOpen(true); }}
                  disabled={location.enrichmentStatus !== 'enriched'}
                  data-testid="preview-location-btn"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Preview
                </Button>
                <PublishButton
                  syncedLocationId={location.id}
                  isEnriched={location.enrichmentStatus === 'enriched'}
                  accountDisabled={!(location.partnerAccount?.enabled ?? false)}
                  isBlocked={isBlocked}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Viewer Modal - triggered from CoverPhotoSelector */}
      {(location.partnerImages?.length ?? 0) > 0 && (
        <ImageViewer
          images={sortedPartnerImages}
          initialIndex={selectedImageIndex}
          open={imageViewerOpen}
          onOpenChange={setImageViewerOpen}
        />
      )}

      {/* AI Content Panel — WordPress partners only (panel handles its own visibility) */}
      <AiContentPanel location={location} />

      {/* Main Content Grid: Enrichment (Left) + Partner Reference (Right) */}
      <div className="grid gap-6 lg:grid-cols-5">
        {/* Left Column: Enrichment Form + AI Tags (3/5 = 60%) */}
        <div className="lg:col-span-3 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ScoutAround Enrichment</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingEnrichment ? (
                <div className="space-y-4">
                  <div className="h-10 animate-pulse rounded bg-muted" />
                  <div className="h-10 animate-pulse rounded bg-muted" />
                  <div className="h-10 animate-pulse rounded bg-muted" />
                </div>
              ) : (
                <EnrichmentForm
                  key={location.id}
                  syncedLocationId={location.id}
                  existingEnrichment={enrichment ?? undefined}
                  isPublished={publicationStatus?.isPublished ?? false}
                  locationName={location.name}
                  partnerPropertyType={location.propertyType}
                  partnerCancellationPolicy={location.partnerCancellationPolicy}
                  partnerImages={sortedPartnerImages}
                  partnerDefaultOwner={location.partnerAccount?.defaultOwner}
                  pricingFormulaEnabled={location.partnerAccount?.pricingFormulaEnabled ?? false}
                  defaultMinimumBookingHours={location.partnerAccount?.partnerType?.defaultMinimumBookingHours ?? 12}
                  defaultInstantBooking={location.partnerAccount?.partnerType?.defaultInstantBooking ?? true}
                  onImageView={(index) => {
                    setSelectedImageIndex(index);
                    setImageViewerOpen(true);
                  }}
                  tagData={{
                    confirmedImageTags: tagData?.confirmedImageTags ?? {},
                    manualImageTags: tagData?.manualImageTags,
                    imageSuggestions: tagData?.suggestions.images ?? {},
                    imageRuns: tagData?.runs?.images,
                    allSuggestions: tagData?.suggestions ?? {},
                    availableTags: availableTags,
                    rejectedTags: tagData?.rejectedTags ?? [],
                    processingTags,
                    removingTags,
                  }}
                  onAcceptTag={handleAcceptImageTag}
                  onRejectTag={handleRejectImageTag}
                  onRemoveTag={handleRemoveImageTag}
                  tagAnalysisStatus={tagData?.status}
                  onReanalyzeAllImages={handleReanalyzeAllImages}
                  isReanalyzingAllImages={reanalyzeAllImagesMutation.isPending}
                  onReanalyzeSingleImage={handleReanalyzeSingleImage}
                  isReanalyzingSingleImage={reanalyzingImageIndex !== undefined}
                  reanalyzingImageIndex={reanalyzingImageIndex}
                  onRemoveImage={isFileImportPartner(location.partnerAccount?.partnerType?.code) ? handleRemoveImage : undefined}
                  removingImageIndex={bulkRemovalProgress ? -1 : removingImageIndex}
                  onRemoveImages={
                    isFileImportPartner(location.partnerAccount?.partnerType?.code)
                      ? (urls) => { void removeImages(urls); }
                      : undefined
                  }
                  bulkRemovalProgress={bulkRemovalProgress}
                  isGeneratingType={location.isGeneratingType}
                  typeAiConfidence={location.typeAiConfidence}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Partner Reference Data (2/5 = 40%) */}
        <div className="space-y-6 lg:col-span-2">
          {/* Property Quick Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Home className="h-5 w-5" />
                Property Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Property Type */}
              {location.propertyType && (
                <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                  <span className="text-sm text-muted-foreground">Type</span>
                  <span className="font-medium">{location.propertyType}</span>
                </div>
              )}

              {/* Quick Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                {location.bedrooms !== null && (
                  <div className="text-center rounded-lg border p-3">
                    <p className="text-2xl font-bold">{location.bedrooms}</p>
                    <p className="text-xs text-muted-foreground">Bedrooms</p>
                  </div>
                )}
                {location.bathrooms !== null && (
                  <div className="text-center rounded-lg border p-3">
                    <p className="text-2xl font-bold">{location.bathrooms}</p>
                    <p className="text-xs text-muted-foreground">Bathrooms</p>
                  </div>
                )}
                {location.accommodates !== null && (
                  <div className="text-center rounded-lg border p-3">
                    <p className="text-2xl font-bold">{location.accommodates}</p>
                    <p className="text-xs text-muted-foreground">Guests</p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-muted-foreground">Description</span>
                  <div className="flex items-center gap-2">
                    {location.descriptionSource === 'ai' && (
                      <Badge variant="secondary" className="text-xs" data-testid="description-source-ai">
                        <Sparkles className="mr-1 h-3 w-3" />
                        AI Generated
                      </Badge>
                    )}
                    {location.descriptionSource === 'partner' && (
                      <Badge variant="outline" className="text-xs" data-testid="description-source-partner">
                        From Partner
                      </Badge>
                    )}
                  </div>
                </div>
                {location.description ? (
                  <p className="mt-1 text-sm leading-relaxed">{location.description}</p>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground italic">No description available</p>
                )}
              </div>

              {/* Amenities */}
              {(location.amenities?.length ?? 0) > 0 && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Partner Amenities</span>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(amenitiesExpanded ? (location.amenities ?? []) : (location.amenities ?? []).slice(0, 8)).map((amenity) => (
                      <Badge key={amenity} variant="secondary" className="text-xs">
                        {amenity}
                      </Badge>
                    ))}
                    {(location.amenities?.length ?? 0) > 8 && (
                      <button
                        type="button"
                        onClick={() => { setAmenitiesExpanded(!amenitiesExpanded); }}
                        className="inline-flex items-center rounded-full border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        {amenitiesExpanded ? 'Show less' : `+${String((location.amenities?.length ?? 0) - 8)} more`}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Partner Taxonomy (reference-only categories/tags) */}
          <PartnerTaxonomyCard
            categoriesRaw={location.partnerCategoriesRaw}
            tagsRaw={location.partnerTagsRaw}
            categories={location.categories}
            tags={location.tags}
            amenities={location.partnerAmenities}
          />
          <PinAddressCard pinAddress={location.pinAddress} printed={location.address?.full ?? null} />
          <StreetLevelCard streetViewUrl={location.streetViewUrl} mapillaryUrl={location.mapillaryUrl} />

          {/* Partner Pricing */}
          <PricingInfoCard pricing={location.pricing} defaultCollapsed />

          {/* Location (Address + Coordinates) */}
          <CollapsibleSection
            title="Location"
            icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
            defaultCollapsed
          >
            <div className="space-y-4">
              {/* Full Address */}
              {location.address?.full && (
                <div>
                  <p className="font-medium">{location.address.full}</p>
                </div>
              )}

              {/* Address Components */}
              {location.address && (location.address.city ?? location.address.state ?? location.address.country) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {location.address.city && (
                    <span><span className="text-muted-foreground">City:</span> {location.address.city}</span>
                  )}
                  {location.address.state && (
                    <span><span className="text-muted-foreground">State:</span> {location.address.state}</span>
                  )}
                  {location.address.country && (
                    <span><span className="text-muted-foreground">Country:</span> {location.address.country}</span>
                  )}
                </div>
              )}

              {/* Coordinates */}
              {location.address?.latitude != null && location.address.longitude != null && (
                <div className="border-t pt-3">
                  <CopyableCoordinates
                    latitude={location.address.latitude}
                    longitude={location.address.longitude}
                    geohash={location.geohash}
                    embedded
                  />
                </div>
              )}

              {/* No-coordinates notice */}
              {location.address?.city && (location.address.latitude == null || location.address.longitude == null) && (
                <div className="border-t pt-3">
                  <Alert data-testid="no-coordinates-notice">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      No map coordinates — geocoding did not resolve this city.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Partner Account Info */}
          <CollapsibleSection
            title="Partner Account"
            icon={<Link2 className="h-4 w-4 text-muted-foreground" />}
            defaultCollapsed
          >
            <div className="space-y-3">
              <div>
                <p className="font-medium">{location.partnerAccount?.name ?? 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">
                  {location.partnerAccount?.partnerType?.name ?? 'Unknown Partner'}
                </p>
              </div>
              <div className="border-t pt-3">
                <span className="text-sm text-muted-foreground">External ID</span>
                <p className="font-mono text-sm">{location.externalId}</p>
              </div>
              {location.externalUrl && (
                <a
                  href={location.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  View on partner platform
                </a>
              )}
            </div>
          </CollapsibleSection>

          {/* Host Contact (only shown if contact info is available) */}
          <HostContactCard hostContact={location.hostContact} />

          {/* Sync Timeline */}
          <SyncTimestampsCard
            firstSyncedAt={location.firstSyncedAt}
            lastSyncedAt={location.lastSyncedAt}
            createdAt={location.createdAt}
            updatedAt={location.updatedAt}
          />
        </div>
      </div>

      {/* Preview Dialog */}
      <LocationPreviewDialog
        syncedLocationId={location.id}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />

      {/* Block Dialog */}
      <BlockDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        onConfirm={handleBlock}
        isLoading={blockMutation.isPending}
        locationName={location.name}
      />
    </div>
  );
}
